import { useEffect, useRef } from "react"
import classNames from "classnames"

import { v4 as uuid } from "uuid"

import { Icon } from "web/components"

function Prompt({ kind }) {
  return (
    <>
      <p className="pointer-events-none m-2.5 text-sm">{`Upload ${kind}`}</p>
      <div>
        <Icon
          name="cloud-arrow-up"
          outline="true"
          width="64px"
          height="64px"
          className="text-neutral-7"
        />
      </div>
      <p className="pointer-events-none m-2.5 text-sm">
        Drag here or click to upload.
      </p>
    </>
  )
}

export default function FileDropZone({
  fullPage,
  onFilesDropped,
  acceptFileTypes,
  disabled,
  kind = "files",
  border = true,
  children,
  renderInFullPage = () => {},
}) {
  const { current: inputId } = useRef(uuid())
  const { current: fileDropZoneId } = useRef(uuid())
  const dropCounter = useRef(0)

  const dragOverClass = "bg-green-50"

  useEffect(() => {
    function handleWindowDragEnter(e) {
      e.preventDefault()
      e.stopPropagation()

      if (e.dataTransfer.types.indexOf("Files") !== -1) {
        const fileZone = document.getElementById(fileDropZoneId)

        fileZone.style.top = `${window.scrollY}px`
        fileZone.style.left = `${window.scrollX}px`
        fileZone.style.visibility = "visible"
      }
    }

    if (fullPage) {
      window.addEventListener("dragenter", handleWindowDragEnter)
    }
    return () => {
      if (fullPage) {
        window.removeEventListener("dragenter", handleWindowDragEnter)
      }
    }
  }, [fileDropZoneId, fullPage])

  function handleDrop(event) {
    if (disabled) return

    event.stopPropagation()
    event.preventDefault()

    document.getElementById(fileDropZoneId).classList.remove(dragOverClass)

    if (fullPage) {
      event.target.style.visibility = "hidden"
    }

    const files = []

    const dt = event.dataTransfer
    if (dt.items) {
      for (let i = 0; i < dt.items.length; i++) {
        if (dt.items[i].kind === "file") {
          const file = dt.items[i].getAsFile()
          files.push(file)
        }
      }
    } else {
      for (let i = 0; i < dt.files.length; i++) {
        files.push(dt.files[i])
      }
    }

    onFilesDropped(files)
  }

  function handleDragEnter(event) {
    if (disabled) return

    event.stopPropagation()
    event.preventDefault()

    document.getElementById(fileDropZoneId).classList.add(dragOverClass)
    dropCounter.current++
  }

  function handleDragLeave(event) {
    if (disabled) return

    event.stopPropagation()
    event.preventDefault()

    dropCounter.current--
    if (dropCounter.current === 0) {
      document.getElementById(fileDropZoneId).classList.remove(dragOverClass)
    }

    if (fullPage) {
      event.target.style.visibility = "hidden"
    }
  }

  function handleFileInputChange(event) {
    event.preventDefault()

    const files = [...event.target.files]

    onFilesDropped(files)
    event.target.value = null
  }

  function handleDropZoneClick(event) {
    if (disabled) return

    event.preventDefault()
    event.stopPropagation()

    if (fullPage) return
    openFileInput()
  }

  function openFileInput() {
    const fileInput = document.getElementById(inputId)
    fileInput.click()
  }

  const hasChildren = children !== undefined
  const showPrompt = !hasChildren && !fullPage

  const containerClasses = classNames("h-full", {
    "box-border flex cursor-pointer flex-col items-center justify-center rounded-md text-neutral-7 hover:border-2 hover:border-dashed hover:border-neutral-6":
      !hasChildren,
    "invisible fixed top-0 left-0 h-screen w-screen border-none opacity-50":
      fullPage,
    "border-2 border-dashed border-neutral-8": border && !hasChildren,
  })

  const containerStyles = hasChildren
    ? undefined
    : { gridTemplateRows: "1fr auto 1fr" }

  return (
    <>
      <div
        id={fileDropZoneId}
        className={containerClasses}
        style={containerStyles}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={handleDropZoneClick}
      >
        {showPrompt && <Prompt kind={kind} />}
        {hasChildren && children}
      </div>
      <input
        type="file"
        disabled={disabled}
        multiple
        accept={acceptFileTypes}
        id={inputId}
        className="hidden"
        onChange={handleFileInputChange}
        data-testid="input-hidden-file-drop-zone"
      />
      {fullPage && renderInFullPage({ onSelectFiles: openFileInput })}
    </>
  )
}
